import React, { useMemo, useState } from "react";
import { Modal, Button, Row, Col, Select, Tag, Space, Typography } from "antd";
import { ArrowDownOutlined, InfoCircleOutlined } from "@ant-design/icons";

const { Option } = Select;
const { Text } = Typography;

interface DeductionItem {
  category: string;
  supplier: string;
  location: string;
  description: string;
  amount: number;
  source: "matrix" | "daily_revenue";
  includeInTotal?: boolean;
  customEntries?: { description: string; amount: number }[];
  customValue?: number;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  t: (key: string) => string;
  matrix: any;
  locations: any[];
  locationFilters: string[];
  dailyRevenueDeductions: {
    date: string;
    description: string;
    amount: number;
    location?: string;
  }[];
  getDisplayedTotal: (rowId: string, sup: any) => number;
  getDisplayedCustom?: (rowId: string, sup: any) => number | undefined;
}

const DeductionsDetailsModal: React.FC<Props> = ({
  visible,
  onClose,
  t,
  matrix,
  locations,
  locationFilters,
  dailyRevenueDeductions,
  getDisplayedTotal,
  getDisplayedCustom,
}) => {
  const [filters, setFilters] = useState<{
    category: string;
    supplier: string;
    source: string;
    location: string;
  }>({
    category: "all",
    supplier: "all",
    source: "all",
    location: "all",
  });

  console.log("AAAAdailyRevenueDeductions", dailyRevenueDeductions);
  console.log("AAAAmatrix", matrix);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const getSupplierColor = (name: string) => {
    const palette = [
      "#2f54eb",
      "#13c2c2",
      "#fa8c16",
      "#eb2f96",
      "#52c41a",
      "#722ed1",
      "#fa541c",
    ];
    const safe = name || "S";
    let hash = 0;
    for (let i = 0; i < safe.length; i++) {
      hash = safe.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % palette.length;
    return palette[idx];
  };

  const locationMap = useMemo<Record<string, string>>(
    () =>
      (locations || []).reduce((acc: Record<string, string>, loc: any) => {
        if (loc?._id) acc[loc._id] = loc.name;
        return acc;
      }, {}),
    [locations]
  );

  const { deductionsList, uniqueCategories, uniqueSuppliers, uniqueLocations } =
    useMemo(() => {
      const list: DeductionItem[] = [];
      const allCategoriesSet = new Set<string>();
      const allSuppliersSet = new Set<string>();

      // 1) Daily revenue deductions (paymentFromTheRevenue)
      dailyRevenueDeductions.forEach((ded) => {
        list.push({
          category: t("Daily revenue deductions"),
          supplier: "-",
          location: ded.location
            ? locationMap[ded.location] || ded.location
            : t("Global"),
          description: ded.description || "-",
          amount: ded.amount,
          source: "daily_revenue",
        });
      });

      // 2) Expense matrix totals (already include daily revenue via getDisplayedTotal)
      if (matrix && Array.isArray(matrix.rows)) {
        const activeLoc = locationFilters[0] || null;

        matrix.rows.forEach((row: any) => {
          const categoryName = row.categoryName || "-";
          allCategoriesSet.add(categoryName);

          const grouped: Record<string, any[]> = {};
          // Prikazuj SVE suppliers (i sa i bez locationId)
          (row.suppliers || []).forEach((sup: any) => {
            const supId = sup.supplierId;
            if (!grouped[supId]) grouped[supId] = [];
            grouped[supId].push(sup);
          });

          Object.values(grouped).forEach((entries: any[]) => {
            let chosen: any[] = entries;

            if (activeLoc) {
              // SPECIFIC LOCATION: Include ONLY entries for that location (exclude global)
              const locEntries = entries.filter(
                (e) => (e.locationId || "").toString() === activeLoc
              );
              chosen = locEntries;
            } else {
              // GLOBAL: uključi SAMO unose SA locationId (ne računamo global)
              chosen = entries.filter((e) => e.locationId);
            }

            chosen.forEach((sup: any) => {
              const supplierName = sup.supplierName || "-";
              allSuppliersSet.add(supplierName);

              const locationName =
                locations.find((l) => l._id === sup.locationId)?.name ||
                (sup.locationId ? t("Unknown location") : t("Global"));

              const total = getDisplayedTotal(row.categoryId, sup);
              const customEntries = Array.isArray(sup.customEntries)
                ? sup.customEntries.map((ce: any) => ({
                    description:
                      ce.description || ce.label || t("Custom entry"),
                    amount: Number(ce.amount || 0),
                  }))
                : [];
              const customValue =
                typeof getDisplayedCustom === "function"
                  ? getDisplayedCustom(row.categoryId, sup)
                  : undefined;

              if (total > 0) {
                list.push({
                  category: categoryName,
                  supplier: supplierName,
                  location: locationName,
                  description: t("Total expense"),
                  amount: total,
                  source: "matrix",
                  includeInTotal: true,
                  customEntries:
                    customEntries.length > 0 ? customEntries : undefined,
                  customValue:
                    customEntries.length === 0 &&
                    customValue !== undefined &&
                    customValue !== null &&
                    Number(customValue) > 0
                      ? Number(customValue) || 0
                      : undefined,
                });
              }
            });
          });
        });
      }

      const uniqueCategories = Array.from(
        new Set([
          ...Array.from(allCategoriesSet),
          t("Daily revenue deductions"),
        ])
      ).sort();
      const uniqueSuppliers = Array.from(allSuppliersSet).sort();
      const uniqueLocations = Array.from(
        new Set(list.map((d) => d.location))
      ).sort();

      //   console.log("[DeductionsDetailsModal] Generated deductions list:", {
      //     totalItems: list,
      //     dailyRevenueItems: list.filter((d) => d.source === "daily_revenue"),
      //     matrixItems: list.filter((d) => d.source === "matrix"),
      //     totalAmount: list
      //       .reduce((sum, item) => sum + item.amount, 0)
      //       .toLocaleString("sr-RS"),
      //   });

      return {
        deductionsList: list,
        uniqueCategories,
        uniqueSuppliers,
        uniqueLocations,
      };
    }, [
      matrix,
      locationFilters,
      dailyRevenueDeductions,
      getDisplayedTotal,
      t,
      locationMap,
      locations,
    ]);

  const filteredList = useMemo(() => {
    return deductionsList.filter((item) => {
      if (filters.category !== "all" && item.category !== filters.category)
        return false;
      if (filters.supplier !== "all" && item.supplier !== filters.supplier)
        return false;
      if (filters.source !== "all" && item.source !== filters.source)
        return false;
      if (filters.location !== "all" && item.location !== filters.location)
        return false;
      return true;
    });
  }, [deductionsList, filters]);

  const totalAmount = useMemo(() => {
    const total = filteredList
      .filter((item) => item.includeInTotal !== false)
      .reduce((sum, item) => sum + item.amount, 0);

    // console.log("[DeductionsDetailsModal] Filtered total:", {
    //   filteredItems: filteredList.length,
    //   includedInTotal: filteredList.filter(
    //     (item) => item.includeInTotal !== false
    //   ).length,
    //   total: total.toLocaleString("sr-RS"),
    // });

    return total;
  }, [filteredList]);

  const groupedByCategory = useMemo(() => {
    const groups: Record<
      string,
      { items: DeductionItem[]; total: number; id: string }
    > = {};
    filteredList.forEach((item, idx) => {
      const key = item.category || `cat-${idx}`;
      if (!groups[key]) {
        groups[key] = { items: [], total: 0, id: key };
      }
      groups[key].items.push(item);
      if (item.includeInTotal !== false) {
        groups[key].total += item.amount;
      }
    });
    return Object.values(groups);
  }, [filteredList]);

  const handleClose = () => {
    setFilters({
      category: "all",
      supplier: "all",
      source: "all",
      location: "all",
    });
    onClose();
  };

  if (!visible) return null;

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ArrowDownOutlined style={{ color: "#ff4d4f" }} />
          <span>
            {t("Total deductions")} - {t("Detailed breakdown")}
          </span>
        </div>
      }
      open={visible}
      className={"standardHeader standardFooter"}
      onCancel={handleClose}
      width={1400}
      footer={[
        <Button key="close" type="primary" onClick={handleClose}>
          {t("Close")}
        </Button>,
      ]}
    >
      {/* Filters Section */}
      <div
        style={{
          marginBottom: 20,
          padding: "20px 24px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 12,
          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              fontSize: 16,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <InfoCircleOutlined />
            {t("Filter deductions")}
          </Text>
        </div>
        <Row gutter={[12, 12]}>
          <Col span={6}>
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 12, color: "#fff" }}>
                {t("Category")}
              </Text>
            </div>
            <Select
              showSearch
              value={filters.category}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, category: value }))
              }
              style={{ width: "100%" }}
              placeholder={t("Search categories...")}
              filterOption={(input, option: any) => {
                if (option?.value === "all") return true;
                return (option?.value?.toLowerCase() ?? "").includes(
                  input.toLowerCase()
                );
              }}
            >
              <Option value="all">
                <span style={{ fontWeight: 600, color: "#1890ff" }}>
                  {t("All categories")}
                </span>
              </Option>
              {uniqueCategories.map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 12, color: "#fff" }}>
                {t("Supplier")}
              </Text>
            </div>
            <Select
              showSearch
              value={filters.supplier}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, supplier: value }))
              }
              style={{ width: "100%" }}
              placeholder={t("Search suppliers...")}
              filterOption={(input, option: any) => {
                if (option?.value === "all") return true;
                return (option?.value?.toLowerCase() ?? "").includes(
                  input.toLowerCase()
                );
              }}
            >
              <Option value="all">
                <span style={{ fontWeight: 600, color: "#1890ff" }}>
                  {t("All suppliers")}
                </span>
              </Option>
              {uniqueSuppliers.map((sup) => (
                <Option key={sup} value={sup}>
                  {sup}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 12, color: "#fff" }}>
                {t("Source")}
              </Text>
            </div>
            <Select
              showSearch
              value={filters.source}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, source: value }))
              }
              style={{ width: "100%" }}
              placeholder={t("Search sources...")}
              filterOption={(input, option: any) => {
                if (option?.value === "all") return true;
                const label =
                  option?.value === "daily_revenue"
                    ? t("Daily revenue")
                    : option?.value === "matrix"
                    ? t("Matrix")
                    : "";
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              <Option value="all">
                <span style={{ fontWeight: 600, color: "#1890ff" }}>
                  {t("All sources")}
                </span>
              </Option>
              <Option value="daily_revenue">{t("Daily revenue")}</Option>
              <Option value="matrix">{t("Matrix")}</Option>
            </Select>
          </Col>
          <Col span={6}>
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 12, color: "#fff" }}>
                {t("Location")}
              </Text>
            </div>
            <Select
              showSearch
              value={filters.location}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, location: value }))
              }
              style={{ width: "100%" }}
              placeholder={t("Search locations...")}
              filterOption={(input, option: any) => {
                if (option?.value === "all") return true;
                return (option?.value?.toLowerCase() ?? "").includes(
                  input.toLowerCase()
                );
              }}
            >
              <Option value="all">
                <span style={{ fontWeight: 600, color: "#1890ff" }}>
                  {t("All locations")}
                </span>
              </Option>
              {uniqueLocations.map((loc) => (
                <Option key={loc} value={loc}>
                  {loc}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Active Filters & Reset */}
        {(filters.category !== "all" ||
          filters.supplier !== "all" ||
          filters.source !== "all" ||
          filters.location !== "all") && (
          <div
            style={{
              marginTop: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Space wrap size={[8, 8]}>
              {filters.category !== "all" && (
                <Tag
                  color="blue"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, category: "all" }))
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: 13,
                    borderRadius: 16,
                  }}
                >
                  {t("Category")}: {filters.category}
                </Tag>
              )}
              {filters.supplier !== "all" && (
                <Tag
                  color="green"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, supplier: "all" }))
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: 13,
                    borderRadius: 16,
                  }}
                >
                  {t("Supplier")}: {filters.supplier}
                </Tag>
              )}
              {filters.source !== "all" && (
                <Tag
                  color="purple"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, source: "all" }))
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: 13,
                    borderRadius: 16,
                  }}
                >
                  {t("Source")}:{" "}
                  {filters.source === "daily_revenue"
                    ? t("Daily revenue")
                    : t("Matrix")}
                </Tag>
              )}
              {filters.location !== "all" && (
                <Tag
                  color="orange"
                  closable
                  onClose={() =>
                    setFilters((prev) => ({ ...prev, location: "all" }))
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: 13,
                    borderRadius: 16,
                  }}
                >
                  {t("Location")}: {filters.location}
                </Tag>
              )}
            </Space>
            <Button
              type="default"
              size="small"
              onClick={() =>
                setFilters({
                  category: "all",
                  supplier: "all",
                  source: "all",
                  location: "all",
                })
              }
              style={{
                background: "#fff",
                borderRadius: 6,
                fontWeight: 500,
              }}
            >
              {t("Reset filters")}
            </Button>
          </div>
        )}
      </div>

      {/* Summary Section */}
      <div
        style={{
          marginBottom: 16,
          padding: "12px 16px",
          background: "#f5f5f5",
          borderRadius: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text strong style={{ fontSize: 16 }}>
            {t("Total")}: {filteredList.length} {t("items")}
          </Text>
          <Text strong style={{ fontSize: 18, color: "#ff4d4f" }}>
            {totalAmount.toLocaleString("sr-RS")} RSD
          </Text>
        </div>
      </div>

      {/* Grouped accordion by category */}
      <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
        {groupedByCategory.map((group) => {
          const isOpen = expandedCategory === group.id;
          const hasCustomInGroup = group.items.some(
            (item) =>
              (item.customEntries && item.customEntries.length > 0) ||
              (item.customValue !== undefined && item.customValue !== null)
          );
          return (
            <div
              key={group.id}
              style={{
                border: isOpen ? "1px solid #adc6ff" : "1px solid #f0f0f0",
                borderRadius: 10,
                marginBottom: 12,
                overflow: "hidden",
                boxShadow: isOpen
                  ? "0 4px 12px rgba(24,144,255,0.12)"
                  : "0 2px 8px rgba(0,0,0,0.03)",
                background: isOpen
                  ? "linear-gradient(135deg, #f0f5ff 0%, #ffffff 80%)"
                  : "#fff",
                transition: "all 0.2s ease",
              }}
            >
              <div
                role="button"
                onClick={() =>
                  setExpandedCategory((prev) =>
                    prev === group.id ? null : group.id
                  )
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 14px",
                  cursor: "pointer",
                  background: isOpen ? "rgba(24,144,255,0.08)" : "#fafafa",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      display: "inline-block",
                      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      fontWeight: 700,
                      color: isOpen ? "#2f54eb" : "#1f3b73",
                    }}
                  >
                    ▶
                  </span>
                  <Text
                    strong
                    style={{ color: isOpen ? "#1d39c4" : undefined }}
                  >
                    {group.id}
                  </Text>
                  {hasCustomInGroup && (
                    <Tag color="blue" style={{ marginLeft: 4 }}>
                      {t("Custom entries")}
                    </Tag>
                  )}
                </div>
                <Text strong style={{ color: isOpen ? "#d4380d" : "#ff4d4f" }}>
                  {group.total.toLocaleString("sr-RS")} RSD
                </Text>
              </div>

              {isOpen && (
                <div
                  style={{
                    padding: "12px 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  {(() => {
                    const supplierGroups = group.items.reduce(
                      (
                        acc: Record<
                          string,
                          {
                            supplier: string;
                            location: string;
                            items: DeductionItem[];
                            total: number;
                          }
                        >,
                        item
                      ) => {
                        const key = `${item.supplier}-${item.location}`;
                        if (!acc[key]) {
                          acc[key] = {
                            supplier: item.supplier,
                            location: item.location,
                            items: [],
                            total: 0,
                          };
                        }
                        acc[key].items.push(item);
                        if (item.includeInTotal !== false) {
                          acc[key].total += item.amount;
                        }
                        return acc;
                      },
                      {}
                    );
                    const sortedSuppliers = Object.values(supplierGroups).sort(
                      (a, b) => {
                        const locA = (a.location || "").toLowerCase();
                        const locB = (b.location || "").toLowerCase();
                        if (locA === locB) {
                          return (a.supplier || "")
                            .toLowerCase()
                            .localeCompare((b.supplier || "").toLowerCase());
                        }
                        return locA.localeCompare(locB);
                      }
                    );
                    return sortedSuppliers.map((supGroup, sIdx) => (
                      <div
                        key={`${group.id}-supplier-${sIdx}`}
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: 10,
                          background: "#fff",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 14px",
                            background: "#f6f8ff",
                            borderBottom: "1px solid #e6e9f5",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                background: getSupplierColor(supGroup.supplier),
                                color: "#fff",
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textTransform: "uppercase",
                              }}
                            >
                              {supGroup.supplier?.[0] || "S"}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              <Text strong style={{ margin: 0 }}>
                                {supGroup.supplier}
                              </Text>
                              <Text
                                type="secondary"
                                style={{ margin: 0, fontSize: 12 }}
                              >
                                {supGroup.location}
                              </Text>
                            </div>
                          </div>
                          <Text strong style={{ color: "#ff4d4f" }}>
                            {t("Supplier total")}:{" "}
                            {supGroup.total.toLocaleString("sr-RS")} RSD
                          </Text>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "8px 10px",
                            gap: 8,
                          }}
                        >
                          {supGroup.items.map((item, idx) => {
                            const rowKey = `${group.id}-${supGroup.supplier}-${supGroup.location}-${idx}`;
                            const hasCustom =
                              (item.customEntries &&
                                item.customEntries.length > 0) ||
                              (item.customValue !== undefined &&
                                item.customValue !== null);
                            const expanded = expandedRows.has(rowKey);

                            return (
                              <div
                                key={rowKey}
                                style={{
                                  border: "1px solid #f0f0f0",
                                  borderRadius: 8,
                                  padding: "10px 12px",
                                  background: "#fcfcfc",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 8,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 12,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          padding: "4px 8px",
                                          borderRadius: 4,
                                          fontSize: 12,
                                          background:
                                            item.source === "daily_revenue"
                                              ? "#e6f7ff"
                                              : "#f6ffed",
                                          color:
                                            item.source === "daily_revenue"
                                              ? "#1890ff"
                                              : "#52c41a",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {item.source === "daily_revenue"
                                          ? t("Daily revenue")
                                          : t("Matrix")}
                                      </span>
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 2,
                                        }}
                                      >
                                        <Text strong style={{ margin: 0 }}>
                                          {item.description}
                                        </Text>
                                        <Text
                                          type="secondary"
                                          style={{ margin: 0, fontSize: 12 }}
                                        >
                                          {item.location}
                                        </Text>
                                      </div>
                                    </div>
                                    <Button
                                      size="small"
                                      type="text"
                                      disabled={!hasCustom}
                                      icon={
                                        <span
                                          style={{
                                            display: "inline-block",
                                            transform: expanded
                                              ? "rotate(90deg)"
                                              : "rotate(0deg)",
                                            opacity: hasCustom ? 1 : 0.2,
                                            transition: "transform 0.2s ease",
                                          }}
                                        >
                                          ▶
                                        </span>
                                      }
                                      aria-label={
                                        expanded ? t("Hide") : t("Show")
                                      }
                                      onClick={() => {
                                        if (!hasCustom) return;
                                        setExpandedRows((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(rowKey))
                                            next.delete(rowKey);
                                          else next.add(rowKey);
                                          return next;
                                        });
                                      }}
                                      style={{
                                        width: 28,
                                        height: 28,
                                        opacity: hasCustom ? 1 : 0.2,
                                        marginTop: -4,
                                      }}
                                    />
                                  </div>

                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    <Text
                                      strong
                                      style={{
                                        color:
                                          item.includeInTotal === false
                                            ? "#8c8c8c"
                                            : "#1f3b73",
                                      }}
                                    >
                                      {item.amount.toLocaleString("sr-RS")} RSD
                                    </Text>
                                  </div>
                                </div>

                                {expanded && hasCustom && (
                                  <div
                                    style={{
                                      background: "#fafafa",
                                      border: "1px solid #f0f0f0",
                                      borderRadius: 8,
                                      padding: "12px",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 12,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "grid",
                                        gap: 8,
                                        background: "#fff",
                                        border: "1px solid #f0f0f0",
                                        borderRadius: 8,
                                        padding: "12px",
                                      }}
                                    >
                                      {item.customEntries &&
                                        item.customEntries.length > 0 &&
                                        item.customEntries.map((ce, cIdx) => (
                                          <div
                                            key={`${rowKey}-ce-${cIdx}`}
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              gap: 12,
                                              padding: "8px 10px",
                                              borderRadius: 6,
                                              background: "#f6f9ff",
                                              border: "1px solid #e6f0ff",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                              }}
                                            >
                                              <Text
                                                strong
                                                style={{ margin: 0 }}
                                              >
                                                {ce.description}
                                              </Text>
                                              <Text
                                                type="secondary"
                                                style={{
                                                  margin: 0,
                                                  fontSize: 12,
                                                }}
                                              >
                                                {t("Custom entry")}
                                              </Text>
                                            </div>
                                            <Text
                                              strong
                                              style={{ color: "#1f3b73" }}
                                            >
                                              {Number(
                                                ce.amount || 0
                                              ).toLocaleString("sr-RS")}{" "}
                                              RSD
                                            </Text>
                                          </div>
                                        ))}

                                      {item.customValue !== undefined &&
                                        item.customValue !== null && (
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              gap: 12,
                                              padding: "10px 12px",
                                              borderRadius: 6,
                                              background: "#fff7e6",
                                              border: "1px solid #ffe0b3",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                              }}
                                            >
                                              <Text
                                                strong
                                                style={{ margin: 0 }}
                                              >
                                                {t("Custom value")}
                                              </Text>
                                              <Text
                                                type="secondary"
                                                style={{
                                                  margin: 0,
                                                  fontSize: 12,
                                                }}
                                              >
                                                {t("Applied to this supplier")}
                                              </Text>
                                            </div>
                                            <Text
                                              strong
                                              style={{ color: "#d48806" }}
                                            >
                                              {Number(
                                                item.customValue || 0
                                              ).toLocaleString("sr-RS")}{" "}
                                              RSD
                                            </Text>
                                          </div>
                                        )}

                                      {!item.customEntries?.length &&
                                        (item.customValue === undefined ||
                                          item.customValue === null) && (
                                          <Text
                                            type="secondary"
                                            style={{ fontSize: 12 }}
                                          >
                                            {t("No additional entries")}
                                          </Text>
                                        )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default DeductionsDetailsModal;
